import { h } from 'preact';
import type { CuePoint } from './types/cuepoint';
import type { SidePanelsManager, UpperBarManager } from '@playkit-js/ui-managers';
import { PLUGIN_ICON_PATH } from './components/icons';
import { PluginButton } from './components/plugin-button/plugin-button';
import { SidePanelModes, SidePanelPositions, ReservedPresetNames } from '@playkit-js/playkit-js-ui';
import { Panel } from './components/transcript-panel/panel';
import { DataProvider } from './providers/data-provider';
import { Text, withText } from 'preact-i18n';

export const pluginName = 'taxonomyTranscript';

interface TimedMetadataEvent {
  payload: {
    cues: CuePoint[];
  };
}

export class TaxonomyTranscript extends KalturaPlayer.core.BasePlugin {
  private transcriptSidePanel: number | null = null;
  private transcriptButton: number | null = null;
  private isActive = false;
  private pluginButtonRef: HTMLButtonElement | null = null;
  private dataProvider: DataProvider | null = null;
  private activeCuePoint: CuePoint | null = null;

  private get sidePanelManager(): SidePanelsManager | undefined {
    return this.player.getService('sidePanelsManager') as SidePanelsManager | undefined;
  }

  private get upperBarManager(): UpperBarManager | undefined {
    return this.player.getService('upperBarManager') as UpperBarManager | undefined;
  }

  private get cuePointManager(): any {
    return this.player.getService('kalturaCuepoints') as any;
  }

  public static isValid(): boolean {
    return true;
  }

  private _setPluginButtonRef = (ref: HTMLButtonElement | null): void => {
    this.pluginButtonRef = ref;
  };

  public loadMedia(): void {
    if (!this.validateDependencies()) {
      return;
    }

    this.dataProvider = new DataProvider({
      config: this.config,
      logger: this.logger,
      player: this.player,
      entryId: this.player.sources.id
    });
    this.dataProvider.fetchData();
    this.addTranscriptComponent();
    this.cuePointManager.registerTypes([this.cuePointManager.CuepointType.CAPTION]);
    this._initListeners();
  }

  private validateDependencies(): boolean {
    if (!this.sidePanelManager) {
      this.logger.warn('Missing dependency: `sidePanelsManager`');
      return false;
    }

    if (!this.upperBarManager) {
      this.logger.warn('Missing dependency: `upperBarManager`');
      return false;
    }

    return true;
  }

  private addTranscriptComponent(): void {
    const translations = {
      showTranscript: <Text id="taxonomyTranscript.showTranscript">Show Transcript</Text>,
      hideTranscript: <Text id="taxonomyTranscript.hideTranscript">Hide Transcript</Text>
    };
    this.transcriptButton = this.upperBarManager?.add({
      ariaLabel: 'Transcript',
      displayName: 'Transcript',
      order: 10,
      component: withText(translations)(({ showTranscript, hideTranscript }: { showTranscript: string; hideTranscript: string }) => {
        const isActive = this.isPluginActive();
        return <PluginButton isActive={isActive} label={this.isPluginActive() ? hideTranscript : showTranscript} setRef={this._setPluginButtonRef} />;
      }),
      onClick: () => {
        if (!this.transcriptSidePanel) {
          return;
        }

        this.isActive = !this.isActive;
        if (this.isActive) {
          this.sidePanelManager?.activateItem(this.transcriptSidePanel);
        } else {
          this.sidePanelManager?.deactivateItem(this.transcriptSidePanel);
        }
      },
      svgIcon: { path: PLUGIN_ICON_PATH }
    }) as number;
  }

  private isPluginActive(): boolean {
    return this.sidePanelManager!.isItemActive(this.transcriptSidePanel as number);
  }

  private closeSidePanel(): void {
    this.isActive = false;

    if (!this.sidePanelManager) {
      return;
    }

    this.sidePanelManager.deactivateItem(this.transcriptSidePanel!);
  }

  private _initListeners(): void {
    this.eventManager.listenOnce(this.player, this.player.Event.TRACKS_CHANGED, () => {
      if (this.player.getTracks(this.player.Track.TEXT).length) {
        this.eventManager.listen(this.player, this.player.Event.TIMED_METADATA_ADDED, this._onTimedMetadataAdded);
        this.eventManager.listen(this.player, this.player.Event.TIMED_METADATA_CHANGE, this._onTimedMetadataChange);
      }
    });
  }

  private _onTimedMetadataAdded = (): void => {
    this.transcriptSidePanel = this.sidePanelManager?.add({
      position: SidePanelPositions.RIGHT,
      panelComponent: () => (
        <Panel
          activeCuePoint={this.activeCuePoint}
          dataProvider={this.dataProvider}
          isActive={this.isPluginActive()}
          player={this.player}
          onCloseButtonClick={() => {
            this.closeSidePanel();
          }}
        />
      ),
      expandMode: SidePanelModes.ALONGSIDE,
      label: ' ',
      presets: [ReservedPresetNames.Playback, ReservedPresetNames.Live]
    }) as number;
  };

  private _onTimedMetadataChange = ({ payload }: TimedMetadataEvent): void => {
    this.activeCuePoint = payload.cues.slice().reverse()[0];
    this.sidePanelManager?.update(this.transcriptSidePanel as number);
  };

  public reset(): void {
    this.dataProvider = null;
  }

  public destroy(): void {
    this.dataProvider = null;
  }
}
